import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
    Button,
    ButtonBase,
    Collapse,
    IconButton,
    makeStyles,
    Modal,
    Typography,
} from '@material-ui/core';

import HtmlContent from '../../HtmlContent';
import { getStylesFromSettings } from '../utils';
import { isIe } from '../../../browserUtils';
import { CloseIcon } from '../../../icons';
import { parseBoolString } from '../../../stringUtils';

// eslint-disable-next-line import/no-cycle -- ok because ReadMore can display content via TaskContent
import TaskContent from '../TaskContent';
import Image from '../../Image';

const useStyles = makeStyles(theme => ({
    wrapper: {
        margin: theme.spacing(2, 0),
    },
    noMargins: {
        margin: 0,
    },
    modalContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 600 - theme.spacing(4),
        maxWidth: '100%',
        minHeight: theme.spacing(50),
        maxHeight: '80%',
        background: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        height: isIe ? '100%' : undefined,

        [theme.breakpoints.down('xs')]: {
            minWidth: 0,
            width: `calc(100% - ${theme.spacing(4)}px)`,
        },
    },
    wide: {
        minWidth: '80%',
    },
    modalHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        background: theme.youwell.colors.secondaryBackground,
    },
    modalHeaderText: {
        flex: '1 1 auto',
        fontWeight: theme.typography.fontWeightMedium,
    },
    modalContent: {
        flex: '1 1 auto',
        padding: theme.spacing(4),
        overflowY: 'auto',
    },
    inlineContainer: {
        margin: theme.spacing(0, 0, 2, 0),
        border: theme.youwell.borders.dim,
        background: theme.palette.background.paper,
    },
    inlineContent: {
        flex: '1 1 auto',
        padding: theme.spacing(2),
        overflowY: 'auto',
    },
    linkWrapper: {
        display: 'flex',
        alignItems: 'flex-end',
    },
    ingressImage: {
        marginRight: theme.spacing(2),
        width: 200,

        [theme.breakpoints.down('xs')]: {
            width: 150,
        },
    },
    button: {

    },
    link: {
        display: 'flex',
        alignItems: 'center',
        transition: 'padding 0.2s, background-color 0.5s',
        borderBottom: theme.youwell.borders.primary,
        paddingBottom: 1,

        '&$active': {
            background: theme.youwell.colors.secondaryBackground,
            padding: theme.spacing(1),
            width: '100%',
            borderBottom: theme.youwell.borders.primary,
        },
    },
    linkText: {
        flex: '1 1 auto',
        textAlign: 'left',
    },
    active: {},
}), { name: 'ReadMore' });

const readMoreTypes = {
    inline: 'inline',
    popup: 'popup',
};

const readMoreLinkTypes = {
    button: 'button',
    link: 'link',
};

const ReadMore = ({ element, getProgramItem, ...props }) => {
    const classes = useStyles();
    const [showReadMore, setShowReadMore] = React.useState(false);

    const toggle = () => {
        setShowReadMore(prev => !prev);
    }

    if (!element) {
        return null;
    }

    const noMargins = parseBoolString(element.settings?.noMargins);
    const linkText = element.settings?.linkText || 'Les mer';
    const headerText = element.settings?.title || element.title || linkText;
    const ingressImage = element.settings?.ingressImage;
    const activeInline = showReadMore && element.settings?.type === readMoreTypes.inline;

    const renderLinkElement = () => {
        switch (element.settings?.linkType) {
        case readMoreLinkTypes.button:
            return (
                <Button onClick={toggle} variant="outlined" className={classnames(classes.button)}>
                    {linkText}
                </Button>
            )
        case readMoreLinkTypes.link:
        default:
            return (
                <ButtonBase onClick={toggle} className={classnames(classes.link, { [classes.active]: activeInline })} disableRipple disableTouchRipple>
                    <Typography className={classes.linkText}>
                        {linkText}
                    </Typography>
                    {activeInline && <CloseIcon />}
                </ButtonBase>
            )
        }
    }

    const renderContent = () => {
        if (element.settings?.contentTaskId) {
            const contentTask = getProgramItem(element.settings?.contentTaskId);
            // order of props is important! "task={contentTask}" must override {...props}
            return (<TaskContent {...props} task={contentTask} getProgramItem={getProgramItem} />);
        }
        return (<HtmlContent html={element.htmlContent} />);
    }

    const renderContainerElement = () => {
        switch (element.settings?.type) {
        case readMoreTypes.inline:
            return (
                <Collapse in={showReadMore} timeout="auto" unmountOnExit>
                    <div className={classnames(classes.inlineContainer, { [classes.noMargins]: noMargins })}>
                        <div className={classes.inlineContent} style={getStylesFromSettings(element.settings)}>
                            {renderContent()}
                        </div>
                    </div>
                </Collapse>
            );
        case readMoreTypes.popup:
        default:
            return (
                <Modal
                    aria-labelledby="modal-window"
                    open={showReadMore}
                    onClose={() => setShowReadMore(false)}
                >
                    <div className={classnames(classes.modalContainer, { [classes.wide]: element.settings?.wide || element.settings?.contentTaskId })}>
                        <div className={classes.modalHeader}>
                            <Typography variant="h6" className={classes.modalHeaderText} id="modal-window">
                                {headerText}
                            </Typography>
                            <IconButton onClick={() => setShowReadMore(false)}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <div className={classes.modalContent} style={getStylesFromSettings(element.settings)}>
                            {renderContent()}
                        </div>
                    </div>
                </Modal>
            );
        }
    }

    return (
        <div className={classnames(classes.wrapper, { [classes.noMargins]: noMargins })}>
            <div className={classes.linkWrapper}>
                {ingressImage && !activeInline && <Image src={ingressImage} className={classes.ingressImage} />}
                {renderLinkElement()}
            </div>
            {renderContainerElement()}
        </div>
    );
}

ReadMore.propTypes = {
    element: PropTypes.object.isRequired,
    getProgramItem: PropTypes.func.isRequired,
};

export default ReadMore;
